/**
 * Resource section taxonomy.
 *
 * Each section has:
 *   slug:   URL slug under /usefull/<slug>
 *   article_category: the value stored in `articles.category` for items
 *                     that belong to this section. Reuses the existing
 *                     Articles CMS — no schema migration needed.
 *   icon:   lucide-react icon component used on the hub + section pages
 *   en/gr:  bilingual labels (also drives Header dropdown order)
 *   blurb:  short description on the hub card
 *
 * The order of this array IS the menu order.
 */
import {
  BookOpen,        // Good to Know
  Backpack,        // Equipment Guide
  ListChecks,      // Difficulty Grades
  HeartHandshake,  // Volunteers
  Users,           // Who we are
  ShieldCheck,     // Regulations & Safety
  BookHeart,       // Stories & Reads
  Archive,         // Ascents/Events Archive
} from 'lucide-react';

export const RESOURCE_SECTIONS = [
  {
    slug: 'volunteers',
    article_category: null,
    icon: HeartHandshake,
    en: { label: 'Volunteers',             blurb: 'Join one of our teams — trail maintenance, route bolting, events, first aid and more.' },
    gr: { label: 'Εθελοντές',              blurb: 'Γίνε μέλος σε μία από τις ομάδες μας — συντήρηση μονοπατιών, ασφάλιση διαδρομών, εκδηλώσεις, Α΄ Βοήθειες και άλλα.' },
    kind: 'static',
    static_path: '/volunteers',
  },
  {
    slug: 'who-we-are',
    article_category: null,
    icon: Users,
    en: { label: 'Who we are',             blurb: 'Our story, mission, history and contact details.' },
    gr: { label: 'Ποιοι είμαστε',          blurb: 'Η ιστορία μας, η αποστολή μας και τα στοιχεία επικοινωνίας.' },
    kind: 'static',
    static_path: '/about',
  },
  {
    slug: 'good-to-know',
    article_category: 'good-to-know',
    icon: BookOpen,
    en: { label: 'Good to Know',           blurb: 'Why join, how ascents work, choosing the right trip, packing your backpack.' },
    gr: { label: 'Καλό να ξέρεις',         blurb: 'Γιατί να γίνεις μέλος, πώς οργανώνονται οι αναβάσεις, πώς να επιλέξεις την κατάλληλη, πώς να ετοιμάσεις τον σάκο.' },
    intro: {
      en: 'New to the mountains or to the Ilioupolis Alpine Club? This section explains everything you need to know before your first ascent — how our trips are organised, how to read a difficulty grade, how to choose an activity that matches your fitness and experience, and how to pack your backpack correctly. Reading these short guides will help you take part safely and get the most out of every outing with the club.',
      gr: 'Νέος στο βουνό ή στον Ε.Ο.Σ. Ηλιούπολης; Σε αυτή την ενότητα θα βρεις όσα χρειάζεται να ξέρεις πριν από την πρώτη σου ανάβαση — πώς οργανώνονται οι δράσεις μας, πώς διαβάζεις τον βαθμό δυσκολίας, πώς να επιλέξεις δραστηριότητα που ταιριάζει στη φυσική σου κατάσταση και την εμπειρία σου, και πώς να ετοιμάσεις σωστά τον σάκο σου. Οι σύντομοι αυτοί οδηγοί θα σε βοηθήσουν να συμμετέχεις με ασφάλεια και να απολαύσεις κάθε εξόρμηση με τον σύλλογο.',
    },
    kind: 'articles',
  },
  {
    slug: 'equipment-guide',
    article_category: 'equipment-guide',
    icon: Backpack,
    en: { label: 'Equipment Guide',        blurb: 'What gear you need for hikes, ascents, climbing and winter mountaineering.' },
    gr: { label: 'Οδηγός Εξοπλισμού',      blurb: 'Τι εξοπλισμός χρειάζεται για πεζοπορίες, αναβάσεις, αναρρίχηση και χειμερινό βουνό.' },
    intro: {
      en: 'The right equipment keeps you safe and comfortable on the mountain. In this guide we break down the gear you need for each type of activity — day hikes, multi-day expeditions, rock climbing and winter mountaineering — from footwear and clothing layers to backpacks, helmets, harnesses and technical hardware. Whether you are buying your first pair of boots or completing a full alpine kit, these articles help you choose wisely and avoid common mistakes.',
      gr: 'Ο σωστός εξοπλισμός σε κρατά ασφαλή και άνετο στο βουνό. Σε αυτόν τον οδηγό αναλύουμε τον εξοπλισμό που χρειάζεσαι για κάθε τύπο δραστηριότητας — ημερήσιες πεζοπορίες, πολυήμερες εξορμήσεις, αναρρίχηση βράχου και χειμερινό βουνό — από τα παπούτσια και τις στρώσεις ρουχισμού μέχρι σακίδια, κράνη, μποντριέ και τεχνικό υλικό. Είτε αγοράζεις τα πρώτα σου ορειβατικά μποτάκια είτε ολοκληρώνεις πλήρη αλπικό εξοπλισμό, τα άρθρα αυτά σε βοηθούν να επιλέξεις σωστά και να αποφύγεις συνηθισμένα λάθη.',
    },
    kind: 'articles',
  },
  {
    slug: 'difficulty-grades',
    article_category: null,
    icon: ListChecks,
    en: { label: 'Difficulty Grades',      blurb: 'How to read the EOSH grades you see on every trip card.' },
    gr: { label: 'Βαθμοί Δυσκολίας',       blurb: 'Πώς διαβάζεις τους βαθμούς ΕΟΣΗ που βλέπεις σε κάθε ανάβαση.' },
    kind: 'static',                       // dedicated static page
    static_path: '/usefull/difficulty-grades',
  },
  {
    slug: 'regulations-safety',
    article_category: 'regulations-safety',
    icon: ShieldCheck,
    en: { label: 'Regulations & Safety',   blurb: 'Club rules, mountain emergency protocols, insurance, medical clearance.' },
    gr: { label: 'Κανονισμοί & Ασφάλεια',  blurb: 'Κανονισμοί συλλόγου, πρωτόκολλα έκτακτης ανάγκης, ασφάλιση, ιατρική βεβαίωση.' },
    intro: {
      en: 'Safety is at the heart of everything the Ilioupolis Alpine Club does. Here you will find the club\u2019s regulations, the emergency and rescue protocols we follow in the mountains, information on member insurance, and the medical clearance required for our schools and more demanding ascents. We ask every participant to read these guidelines carefully — knowing the rules and procedures protects you, your teammates and the whole group.',
      gr: 'Η ασφάλεια βρίσκεται στον πυρήνα κάθε δράσης του Ε.Ο.Σ. Ηλιούπολης. Εδώ θα βρεις τους κανονισμούς του συλλόγου, τα πρωτόκολλα έκτακτης ανάγκης και διάσωσης που ακολουθούμε στο βουνό, πληροφορίες για την ασφάλιση των μελών και την ιατρική βεβαίωση που απαιτείται για τις σχολές και τις πιο απαιτητικές αναβάσεις. Ζητάμε από κάθε συμμετέχοντα να διαβάσει προσεκτικά αυτές τις οδηγίες — η γνώση των κανόνων και των διαδικασιών προστατεύει εσένα, τους συντρόφους σου και ολόκληρη την ομάδα.',
    },
    kind: 'articles',
  },
  {
    slug: 'stories',
    article_category: 'story',           // reuses existing 'story' value already in db
    icon: BookHeart,
    en: { label: 'Stories & Reads',        blurb: 'Member stories, expedition reports and curated mountain reading.' },
    gr: { label: 'Ιστορίες & Αναγνώσματα', blurb: 'Ιστορίες μελών, αναφορές αναβάσεων και επιλεγμένα κείμενα.' },
    kind: 'articles',
  },
  {
    slug: 'archive',
    article_category: null,
    icon: Archive,
    en: { label: 'Ascents & Events Archive', blurb: 'All past trips and events the club has run since 1995.' },
    gr: { label: 'Αρχείο Αναβάσεων & Εκδηλώσεων', blurb: 'Όλες οι αναβάσεις και εκδηλώσεις που έχει διοργανώσει ο σύλλογος από το 1995.' },
    kind: 'archive',                     // reads db.resources where category=club_archive
  },
];

export const getSection = (slug) => RESOURCE_SECTIONS.find((s) => s.slug === slug) || null;

// Reverse lookup — given an article_category (the value stored on each
// CMS article doc), return the section it lives under. Used by article
// detail pages to render a smart category-aware "← Back" link.
export const findSectionByCategory = (category) =>
  RESOURCE_SECTIONS.find((s) => s.article_category === category) || null;

export const tSectionLabel = (s, lang) => (lang === 'gr' ? s.gr.label : s.en.label);
export const tSectionBlurb = (s, lang) => (lang === 'gr' ? s.gr.blurb : s.en.blurb);
export const tSectionIntro = (s, lang) => (s.intro ? (lang === 'gr' ? s.intro.gr : s.intro.en) : null);
